// Flow Sessions Service for backend communication
import { api } from './api';

export interface FlowSessionDto {
  id: string;
  projectId: string;
  userId: string;
  startTime: string;
  endTime?: string;
  duration?: string; // TimeSpan as ISO 8601 duration string
  preRitualCompleted: boolean;
  postJournalText?: string;
  moodAfter?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface StartFlowSessionRequest {
  projectId: string;
  preRitualCompleted?: boolean;
}

export interface EndFlowSessionRequest {
  journalText?: string;
  moodAfter?: string;
}

export interface UpdateJournalRequest {
  journalText?: string;
  moodAfter?: string;
}

// Flow Sessions API
export const flowSessionsApi = {
  // Get active session for the current user
  getActiveSession: (): Promise<FlowSessionDto | null> => {
    return api.get<FlowSessionDto>('/flowsessions/active').catch(() => null);
  },

  // Get a specific session by ID
  getById: (id: string): Promise<FlowSessionDto> => {
    return api.get<FlowSessionDto>(`/flowsessions/${id}`);
  },

  // Get all sessions for the current user
  getUserSessions: (startDate?: string, endDate?: string): Promise<FlowSessionDto[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const queryString = params.toString();
    return api.get<FlowSessionDto[]>(`/flowsessions${queryString ? `?${queryString}` : ''}`);
  },

  // Get all sessions for a specific project
  getProjectSessions: (projectId: string): Promise<FlowSessionDto[]> => {
    return api.get<FlowSessionDto[]>(`/flowsessions/project/${projectId}`);
  },

  // Start a new flow session
  startSession: (data: StartFlowSessionRequest): Promise<FlowSessionDto> => {
    return api.post<FlowSessionDto>('/flowsessions', data);
  },

  // End a flow session
  endSession: (id: string, data: EndFlowSessionRequest): Promise<FlowSessionDto> => {
    return api.post<FlowSessionDto>(`/flowsessions/${id}/end`, data);
  },

  // Update journal for a completed session
  updateJournal: (id: string, data: UpdateJournalRequest): Promise<FlowSessionDto> => {
    return api.put<FlowSessionDto>(`/flowsessions/${id}/journal`, data);
  },

  // Mark pre-ritual as completed
  markPreRitualCompleted: (id: string): Promise<FlowSessionDto> => {
    return api.post<FlowSessionDto>(`/flowsessions/${id}/pre-ritual`);
  },
};

export default flowSessionsApi;
